"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeDetail } from '@open-mercato/shared/lib/frontend/useOrganizationScope'

const VEHICLE_TYPE_OPTIONS = [
  { value: 'electric', label: 'Electric' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'dual_mode', label: 'Dual Mode' },
  { value: 'shunter', label: 'Shunter' },
]

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'in_use', label: 'In Use' },
]

export default function CreateLocomotivePage() {
  const t = useT()
  const router = useRouter()
  const { organizationId, tenantId } = useOrganizationScopeDetail()

  const fields = React.useMemo<CrudField[]>(() => [
    { id: 'evn', label: t('rolling_stock.locomotives.fields.evn', 'EVN (12 digits)'), type: 'text', required: true, placeholder: '910000000000', maxLength: 12 },
    { id: 'name', label: t('rolling_stock.locomotives.fields.name', 'Name'), type: 'text', required: true, placeholder: 'Vectron MS 193-042' },
    { id: 'series', label: t('rolling_stock.locomotives.fields.series', 'Series'), type: 'text', required: true, placeholder: '193' },
    { id: 'vehicleType', label: t('rolling_stock.locomotives.fields.vehicleType', 'Vehicle Type'), type: 'select', required: true, options: VEHICLE_TYPE_OPTIONS },
    { id: 'status', label: t('rolling_stock.locomotives.fields.status', 'Status'), type: 'select', required: true, options: STATUS_OPTIONS },
    { id: 'weightTonnes', label: t('rolling_stock.locomotives.fields.weightTonnes', 'Weight (tonnes)'), type: 'number', required: true, min: 0 },
    { id: 'maxSpeedKmh', label: t('rolling_stock.locomotives.fields.maxSpeedKmh', 'Max Speed (km/h)'), type: 'number', min: 0 },
    { id: 'powerKw', label: t('rolling_stock.locomotives.fields.powerKw', 'Power (kW)'), type: 'number', min: 0 },
    { id: 'ecmName', label: t('rolling_stock.locomotives.fields.ecmName', 'ECM Name'), type: 'text', required: true },
    { id: 'ecmCertificateId', label: t('rolling_stock.locomotives.fields.ecmCertificateId', 'ECM Certificate ID'), type: 'text' },
    { id: 'ecmValidUntil', label: t('rolling_stock.locomotives.fields.ecmValidUntil', 'ECM Valid Until'), type: 'date' },
    { id: 'notes', label: t('rolling_stock.locomotives.fields.notes', 'Notes'), type: 'textarea' },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'identification',
      title: t('rolling_stock.locomotives.groups.identification', 'Identification'),
      fields: ['evn', 'name', 'series', 'vehicleType', 'status'],
    },
    {
      id: 'technical',
      title: t('rolling_stock.locomotives.groups.technical', 'Technical Specs'),
      fields: ['weightTonnes', 'maxSpeedKmh', 'powerKw'],
    },
    {
      id: 'ecm',
      title: t('rolling_stock.locomotives.groups.ecm', 'Entity in Charge of Maintenance'),
      fields: ['ecmName', 'ecmCertificateId', 'ecmValidUntil'],
    },
    {
      id: 'other',
      title: t('rolling_stock.locomotives.groups.other', 'Other'),
      fields: ['notes'],
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('rolling_stock.locomotives.create.title', 'Add Locomotive')}
          backHref="/backend/rolling-stock/locomotives"
          fields={fields}
          groups={groups}
          initialValues={{ status: 'available' }}
          submitLabel={t('rolling_stock.locomotives.create.submit', 'Create Locomotive')}
          cancelHref="/backend/rolling-stock/locomotives"
          onSubmit={async (values) => {
            const payload = {
              ...values,
              organizationId,
              tenantId,
            }
            const { result: created } = await createCrud<{ id?: string }>('rolling-stock/locomotives', payload)
            flash(t('rolling_stock.locomotives.flash.created', 'Locomotive created'), 'success')
            router.push(`/backend/rolling-stock/locomotives/${created?.id}`)
          }}
        />
      </PageBody>
    </Page>
  )
}
